import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
    FaBan,
    FaBriefcase,
    FaBuilding,
    FaCalendarAlt,
    FaChartLine,
    FaCheck,
    FaClock,
    FaDollarSign,
    FaDownload,
    FaEdit,
    FaEye,
    FaFilter,
    FaMapMarkerAlt,
    FaPlus,
    FaSearch,
    FaTrash,
    FaUsers
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalType, setModalType] = useState('view');
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    requirements: [],
    location: {
      city: '',
      state: '',
      country: '',
      type: 'onsite'
    },
    salary: {
      min: '',
      max: '',
      currency: 'USD',
      period: 'yearly'
    },
    type: 'full-time',
    experience: 'entry',
    skills: [],
    benefits: [],
    category: '',
    remote: false,
    urgent: false
  });
  const { apiCall } = useAuth();
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 24 
      }
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchTerm, filterStatus]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await apiCall('GET', '/api/admin/jobs');
      const jobArray = response.data?.jobs || response.data?.data?.jobs || [];
      const processedJobs = jobArray.map(job => {
        return {
          ...job,
          company: job.company?.name || job.companyName || ''
        };
      });
      setJobs(processedJobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    if (searchTerm) {
      filtered = filtered.filter(job => 
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location?.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(job => {
        switch (filterStatus) {
          case 'active':
            return job.isActive && job.status === 'active';
          case 'inactive':
            return !job.isActive;
          case 'pending':
            return job.status === 'pending';
          case 'expired':
            return job.status === 'expired';
          case 'filled':
            return job.status === 'filled';
          default:
            return true;
        }
      });
    }

    setFilteredJobs(filtered);
    setCurrentPage(1);
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      const response = await apiCall('POST', '/api/admin/jobs', formData);
      if (response.success) {
        toast.success('Job created successfully!', {
          style: {
            borderRadius: '12px',
            background: '#fff',
            color: '#333',
            border: '1px solid #10b981',
          },
          iconTheme: {
            primary: '#10b981',
            secondary: '#fff',
          },
        });
        setShowCreateModal(false);
        resetFormData();
        fetchJobs();
      }
    } catch (error) {
      console.error('Failed to create job:', error);
    }
  };

  const resetFormData = () => {
    setFormData({
      title: '',
      company: '',
      description: '',
      requirements: [],
      location: {
        city: '',
        state: '',
        country: '',
        type: 'onsite'
      },
      salary: {
        min: '',
        max: '',
        currency: 'USD',
        period: 'yearly'
      },
      type: 'full-time',
      experience: 'entry',
      skills: [],
      benefits: [],
      category: '',
      remote: false,
      urgent: false
    });
  };

  const handleStatusChange = async (jobId, action) => {
    try {
      const response = await apiCall('PUT', `/api/admin/jobs/${jobId}/status`, { action });
      if (response.success) {
        fetchJobs();
        toast.success(`Job ${action}d successfully`, {
          style: {
            borderRadius: '12px',
            background: '#fff',
            color: '#333',
            border: '1px solid #10b981',
          },
          iconTheme: {
            primary: '#10b981',
            secondary: '#fff',
          },
        });
      }
    } catch (error) {
      console.error('Failed to update job status:', error);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      try {
        const response = await apiCall('DELETE', `/api/admin/jobs/${jobId}`);
        if (response.success) {
          fetchJobs();
          toast.success('Job deleted successfully', {
            style: {
              borderRadius: '12px',
              background: '#fff',
              color: '#333',
              border: '1px solid #10b981',
            },
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          });
        }
      } catch (error) {
        console.error('Failed to delete job:', error);
      }
    }
  };

  const openModal = (type, job = null) => {
    setModalType(type);
    setSelectedJob(job);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedJob(null);
    setModalType('view');
  };

  const addArrayItem = (field, item) => {
    if (item && item.trim() !== '') {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], item.trim()]
      }));
    }
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getStatusBadge = (job) => {
    if (!job.isActive) return <span className="luxury-badge-danger flex items-center"><FaBan className="mr-1 h-3 w-3" /> Inactive</span>;
    switch (job.status) {
      case 'active': return <span className="luxury-badge-success flex items-center"><FaCheck className="mr-1 h-3 w-3" /> Active</span>;
      case 'pending': return <span className="luxury-badge-warning flex items-center"><FaClock className="mr-1 h-3 w-3" /> Pending</span>;
      case 'expired': return <span className="luxury-badge-danger flex items-center"><FaBan className="mr-1 h-3 w-3" /> Expired</span>;
      case 'filled': return <span className="luxury-badge-primary flex items-center"><FaUsers className="mr-1 h-3 w-3" /> Filled</span>;
      default: return <span className="luxury-badge-primary flex items-center"><FaCheck className="mr-1 h-3 w-3" /> {job.status}</span>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'full-time': return <span className="luxury-badge-success flex items-center"><FaCheck className="mr-1 h-3 w-3" /> Full Time</span>;
      case 'part-time': return <span className="luxury-badge-primary flex items-center"><FaClock className="mr-1 h-3 w-3" /> Part Time</span>;
      case 'contract': return <span className="luxury-badge-warning flex items-center"><FaEdit className="mr-1 h-3 w-3" /> Contract</span>;
      case 'internship': return <span className="luxury-badge-primary flex items-center"><FaUsers className="mr-1 h-3 w-3" /> Internship</span>;
      case 'freelance': return <span className="luxury-badge-warning flex items-center"><FaBriefcase className="mr-1 h-3 w-3" /> Freelance</span>;
      default: return <span className="luxury-badge-primary flex items-center">{type}</span>;
    }
  };

  // Pagination
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  // Calculate stats for dashboard cards
  const activeJobs = jobs.filter(job => job.isActive && job.status === 'active').length;
  const pendingJobs = jobs.filter(job => job.status === 'pending').length;
  const expiredJobs = jobs.filter(job => job.status === 'expired').length;
  const filledJobs = jobs.filter(job => job.status === 'filled').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div 
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600"
        ></motion.div>
      </div>
    );
  }

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-luxury-900 font-montserrat tracking-tight">Jobs Management</h1>
          <p className="text-luxury-600 mt-1 font-poppins">View and manage all job listings across the platform</p>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCreateModal(true)} 
            className="luxury-button-primary text-sm flex items-center font-nunito"
          >
            <FaPlus className="mr-2" />
            Post New Job
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="luxury-button-secondary text-sm flex items-center font-nunito"
          >
            <FaDownload className="mr-2" />
            Export
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        variants={containerVariants}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <motion.div
          variants={itemVariants}
          whileHover={{ 
            y: -5,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          className="bg-white rounded-2xl shadow-lg shadow-primary-500/20 border border-luxury-200/50 p-6 transition-all duration-500"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-luxury-600 font-poppins">Total Jobs</p>
              <p className="text-3xl font-bold text-luxury-900 font-montserrat mt-2">{jobs.length}</p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/30">
              <FaBriefcase className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-xs text-success-600">
            <FaChartLine className="mr-1" />
            <span className="font-medium">+12% </span>
            <span className="text-luxury-500 ml-1">from last month</span>
          </div>
        </motion.div>
        
        <motion.div
          variants={itemVariants}
          whileHover={{ 
            y: -5,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          className="bg-white rounded-2xl shadow-lg shadow-success-500/20 border border-luxury-200/50 p-6 transition-all duration-500"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-luxury-600 font-poppins">Active Jobs</p>
              <p className="text-3xl font-bold text-luxury-900 font-montserrat mt-2">{activeJobs}</p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-success-500 to-success-700 flex items-center justify-center shadow-lg shadow-success-500/30">
              <FaCheck className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-4">
            <div className="w-full bg-luxury-100 h-1.5 rounded-full overflow-hidden">
              <div 
                className="h-full bg-success-500 rounded-full"
                style={{ width: `${jobs.length ? (activeJobs / jobs.length) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="mt-1 flex justify-between text-xs">
              <span className="text-success-600 font-medium">{Math.round(jobs.length ? (activeJobs / jobs.length) * 100 : 0)}% active</span>
              <span className="text-luxury-500">{activeJobs}/{jobs.length}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          whileHover={{ 
            y: -5,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          className="bg-white rounded-2xl shadow-lg shadow-warning-500/20 border border-luxury-200/50 p-6 transition-all duration-500"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-luxury-600 font-poppins">Pending Jobs</p>
              <p className="text-3xl font-bold text-luxury-900 font-montserrat mt-2">{pendingJobs}</p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-warning-500 to-warning-700 flex items-center justify-center shadow-lg shadow-warning-500/30">
              <FaClock className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-2">
            <div className="flex items-center text-xs">
              <FaCalendarAlt className="text-warning-500 mr-1" />
              <span className="text-luxury-600">Latest: </span>
            </div>
            <span className="text-xs font-medium text-luxury-800 font-nunito">
              {jobs.filter(j => j.status === 'pending').length > 0 
                ? new Date(Math.max(...jobs.filter(j => j.status === 'pending').map(j => new Date(j.createdAt)))).toLocaleDateString() 
                : 'None'}
            </span>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          whileHover={{ 
            y: -5,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          className="bg-white rounded-2xl shadow-lg shadow-purple-500/20 border border-luxury-200/50 p-6 transition-all duration-500"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-luxury-600 font-poppins">Job Applicants</p>
              <p className="text-3xl font-bold text-luxury-900 font-montserrat mt-2">
                {/* Sample metric */}
                {jobs.reduce((sum, job) => sum + (job.applications || 0), 0)}
              </p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <FaUsers className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <div className="text-center">
              <p className="text-xs text-luxury-500">Pending</p>
              <p className="font-semibold text-sm text-luxury-900">
                {jobs.reduce((sum, job) => sum + (job.pendingApplications || 0), 0)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-luxury-500">Shortlisted</p>
              <p className="font-semibold text-sm text-luxury-900">
                {jobs.reduce((sum, job) => sum + (job.shortlistedApplications || 0), 0)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-luxury-500">Hired</p>
              <p className="font-semibold text-sm text-luxury-900">
                {jobs.reduce((sum, job) => sum + (job.hiredApplications || 0), 0)}
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Search & Filter */}
      <motion.div 
        variants={itemVariants}
        className="bg-white rounded-2xl shadow-lg border border-luxury-200/50 p-6"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <FaSearch className="absolute left-1 top-1/2 transform -translate-y-1/2 text-luxury-400" />
            <input
              type="text"
              placeholder="Search jobs by title, company or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-luxury-50/50 border border-luxury-200 rounded-xl text-luxury-900 placeholder-luxury-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 font-nunito"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-4 pr-10 py-3 bg-luxury-50/50 border border-luxury-200 rounded-xl text-luxury-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 cursor-pointer font-nunito"
            >
              <option value="all">All Jobs</option>
              <option value="active">Active Jobs</option>
              <option value="pending">Pending Jobs</option>
              <option value="expired">Expired Jobs</option>
              <option value="filled">Filled Jobs</option>
            </select>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => fetchJobs()}
              className="px-5 py-3 bg-primary-100 text-primary-700 font-medium rounded-xl hover:bg-primary-200 transition-all duration-300 flex items-center justify-center font-nunito"
            >
              <FaFilter className="mr-2" />
              Apply
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Jobs Table */}
      <motion.div 
        variants={itemVariants}
        className="bg-white rounded-2xl shadow-lg border border-luxury-200/50 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-luxury-50 to-luxury-100">
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Job</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Company</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Type</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Salary</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Applications</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-luxury-100">
              {currentJobs.length > 0 ? (
                currentJobs.map((job) => (
                  <motion.tr 
                    key={job._id || job.id}
                    whileHover={{ backgroundColor: 'rgba(244, 245, 247, 0.5)' }}
                    className="transition-all duration-200 isolation-auto"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-primary-100 rounded-xl flex items-center justify-center">
                          <FaBriefcase className="h-5 w-5 text-primary-700" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-luxury-900 font-nunito line-clamp-1">
                            {job.title}
                            {job.urgent && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-800">
                                Urgent
                              </span>
                            )}
                          </p>
                          <div className="flex items-center text-xs text-luxury-500 font-nunito mt-1">
                            <FaMapMarkerAlt className="mr-1 h-3 w-3" />
                            {job.location?.city || 'Remote'}{job.location?.state ? `, ${job.location.state}` : ''}
                            {job.remote && (
                              <span className="ml-2 text-primary-600 font-medium">• Remote</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 flex-shrink-0 bg-luxury-100 rounded-lg flex items-center justify-center mr-2">
                          <FaBuilding className="h-4 w-4 text-luxury-600" />
                        </div>
                        <span className="text-sm font-medium text-luxury-900 font-nunito">{job.company}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getTypeBadge(job.type || 'full-time')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-luxury-700 font-nunito">
                        <FaDollarSign className="mr-1 h-3 w-3 text-luxury-500" />
                        {job.salary?.min && job.salary?.max ? (
                          <span>{job.salary.min.toLocaleString()}-{job.salary.max.toLocaleString()}</span>
                        ) : job.salary?.min ? (
                          <span>From {job.salary.min.toLocaleString()}</span>
                        ) : job.salary?.max ? (
                          <span>Up to {job.salary.max.toLocaleString()}</span>
                        ) : (
                          <span className="text-luxury-500">Not specified</span>
                        )}
                        {job.salary?.period && (
                          <span className="ml-1 text-luxury-500">/{job.salary.period}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(job)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-luxury-900 font-nunito">{job.applications || 0}</span>
                        <span className="ml-1 text-xs text-luxury-500">applications</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => openModal('view', job)}
                          className="icon-button text-primary-600 hover:text-primary-800 hover:bg-primary-100/50 rounded-full"
                          title="View Details"
                        >
                          <FaEye />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => openModal('edit', job)}
                          className="icon-button text-warning-600 hover:text-warning-800 hover:bg-warning-100/50 rounded-full"
                          title="Edit Job"
                        >
                          <FaEdit />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDeleteJob(job._id || job.id)}
                          className="icon-button text-danger-600 hover:text-danger-800 hover:bg-danger-100/50 rounded-full"
                          title="Delete Job"
                        >
                          <FaTrash />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <FaBriefcase className="h-8 w-8 text-luxury-400" />
                      <p className="mt-2 text-luxury-600 font-medium font-nunito">No jobs found</p>
                      <p className="text-luxury-500 text-sm font-nunito">Try adjusting your search or filter criteria</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-luxury-50/50 border-t border-luxury-200 flex justify-between items-center">
            <span className="text-sm text-luxury-600 font-nunito">
              Showing {indexOfFirstJob + 1}-{Math.min(indexOfLastJob, filteredJobs.length)} of {filteredJobs.length} jobs
            </span>
            <div className="flex space-x-1">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  currentPage === 1 
                  ? 'bg-luxury-100 text-luxury-400 cursor-not-allowed' 
                  : 'bg-luxury-100 text-luxury-700 hover:bg-luxury-200'
                } font-nunito`}
              >
                Previous
              </motion.button>
              
              {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                // Logic to show the current page and pages around it
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }
                
                return (
                  <motion.button
                    key={idx}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      currentPage === pageNum 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-luxury-100 text-luxury-700 hover:bg-luxury-200'
                    } font-nunito`}
                  >
                    {pageNum}
                  </motion.button>
                );
              })}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  currentPage === totalPages 
                  ? 'bg-luxury-100 text-luxury-400 cursor-not-allowed' 
                  : 'bg-luxury-100 text-luxury-700 hover:bg-luxury-200'
                } font-nunito`}
              >
                Next
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Create Job Modal */}
      {showCreateModal && (
        <div className="luxury-modal" onClick={() => setShowCreateModal(false)}>
          <div className="luxury-modal-content max-w-4xl" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-luxury-900">Create New Job</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-luxury-400 hover:text-luxury-600"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleCreateJob} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Job Title *</label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="luxury-input"
                      placeholder="Senior Software Engineer"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Company *</label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                      className="luxury-input"
                      placeholder="TechCorp Inc."
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Job Description *</label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="luxury-textarea"
                    rows="4"
                    placeholder="Describe the job role, responsibilities, and what you're looking for..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Job Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      className="luxury-select"
                    >
                      <option value="full-time">Full Time</option>
                      <option value="part-time">Part Time</option>
                      <option value="contract">Contract</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Experience Level</label>
                    <select
                      value={formData.experience}
                      onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
                      className="luxury-select"
                    >
                      <option value="entry">Entry Level</option>
                      <option value="mid">Mid Level</option>
                      <option value="senior">Senior Level</option>
                      <option value="executive">Executive</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="luxury-input"
                      placeholder="Technology"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      value={formData.location.city}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        location: { ...prev.location, city: e.target.value }
                      }))}
                      className="luxury-input"
                      placeholder="New York"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      value={formData.location.state}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        location: { ...prev.location, state: e.target.value }
                      }))}
                      className="luxury-input"
                      placeholder="NY"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <input
                      type="text"
                      value={formData.location.country}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        location: { ...prev.location, country: e.target.value }
                      }))}
                      className="luxury-input"
                      placeholder="USA"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Work Type</label>
                    <select
                      value={formData.location.type}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        location: { ...prev.location, type: e.target.value }
                      }))}
                      className="luxury-select"
                    >
                      <option value="onsite">On-site</option>
                      <option value="remote">Remote</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Min Salary</label>
                    <input
                      type="number"
                      value={formData.salary.min}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        salary: { ...prev.salary, min: e.target.value }
                      }))}
                      className="luxury-input"
                      placeholder="50000"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Max Salary</label>
                    <input
                      type="number"
                      value={formData.salary.max}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        salary: { ...prev.salary, max: e.target.value }
                      }))}
                      className="luxury-input"
                      placeholder="80000"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Currency</label>
                    <select
                      value={formData.salary.currency}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        salary: { ...prev.salary, currency: e.target.value }
                      }))}
                      className="luxury-select"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="INR">INR</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        checked={formData.remote}
                        onChange={(e) => setFormData(prev => ({ ...prev, remote: e.target.checked }))}
                        className="mr-2"
                      />
                      Remote Work Available
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        checked={formData.urgent}
                        onChange={(e) => setFormData(prev => ({ ...prev, urgent: e.target.checked }))}
                        className="mr-2"
                      />
                      Urgent Hiring
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="luxury-button-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="luxury-button-primary"
                  >
                    <FaPlus className="mr-2" />
                    Create Job
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Job Modal */}
      {showModal && selectedJob && (
        <div className="luxury-modal" onClick={closeModal}>
          <div className="luxury-modal-content max-w-4xl" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-luxury-900">Job Details</h2>
                <button
                  onClick={closeModal}
                  className="text-luxury-400 hover:text-luxury-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-luxury-900">{selectedJob.title}</h3>
                    <p className="text-luxury-600">{selectedJob.company}</p>
                    <div className="flex space-x-2 mt-2">
                      {getStatusBadge(selectedJob)}
                      {getTypeBadge(selectedJob.type)}
                      {selectedJob.urgent && <span className="luxury-badge-danger">Urgent</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-luxury-900">
                      {selectedJob.salary?.min ? `$${selectedJob.salary.min}${selectedJob.salary.max ? `-$${selectedJob.salary.max}` : '+'} ${selectedJob.salary.currency}` : 'Salary not specified'}
                    </p>
                    <p className="text-sm text-luxury-600">
                      {selectedJob.location?.city}, {selectedJob.location?.state}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-luxury-900 mb-3">Job Description</h4>
                  <p className="text-luxury-700 whitespace-pre-wrap">{selectedJob.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-luxury-900 mb-3">Job Details</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Experience:</span> {selectedJob.experience}</p>
                      <p><span className="font-medium">Category:</span> {selectedJob.category || 'Not specified'}</p>
                      <p><span className="font-medium">Work Type:</span> {selectedJob.location?.type}</p>
                      <p><span className="font-medium">Posted:</span> {new Date(selectedJob.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-luxury-900 mb-3">Application Statistics</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Total Applications:</span> {selectedJob.applications?.length || 0}</p>
                      <p><span className="font-medium">Views:</span> {selectedJob.views || 0}</p>
                      <p><span className="font-medium">Saves:</span> {selectedJob.saves || 0}</p>
                    </div>
                  </div>
                </div>

                {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-luxury-900 mb-3">Requirements</h4>
                    <ul className="list-disc list-inside space-y-1 text-luxury-700">
                      {selectedJob.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Jobs; 